import { useLocale } from "../context/LocaleContext";
import React, { useState, useRef, useEffect } from "react";
import { api, useAuth } from "../context/AuthContext";
import { chatCopy } from "../context/chatCopy";
import { askSources } from "../sourceQuestion";
import { heroContent, quickActions, IMAGES } from "../mock";
import { searchPublicSources, availableResearchRecords, exampleResearchRecords, researchScope } from "../publicSourceSearch";
import suggestionCopy from "../context/researchSuggestionCopy";
import { publicSearchCopy } from "../context/publicSearchCopy";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { Search, ArrowUp, Layers, Sparkles } from "lucide-react";

import AdvancedResearchSearch from "./AdvancedResearchSearch";

export default function Hero() {
  const { t, locale } = useLocale();
  const {user} = useAuth();
  const historyCopy = chatCopy[locale] || chatCopy.tr;
  const [savedChat,setSavedChat] = useState(null);
  useEffect(()=>{requestGeneration.current++;setResult(null);setSavedChat(null);},[user?.user_id]);
  const searchCopy = publicSearchCopy[locale] || publicSearchCopy.tr;
  const requestGeneration = useRef(0);
  const [source, setSource] = useState(heroContent.filters[0]);
  const [query, setQuery] = useState("");
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [advancedOpen,setAdvancedOpen]=useState(false);
  const [publicOnly, setPublicOnly] = useState(false);
  const [result, setResult] = useState(null);
  const suggestionsCopy=suggestionCopy[locale]||suggestionCopy.tr;
  const [testQuestions,setTestQuestions] = useState({userId:null,items:[]});
  useEffect(()=>{let current=true;setTestQuestions({userId:null,items:[]});if(!user?.user_id)return;
    const refresh=()=>Promise.resolve().then(()=>api.get('/video-archive/research-suggestions')).then(r=>{if(current&&Array.isArray(r.data.items))setTestQuestions({userId:user.user_id,items:r.data.items});}).catch(()=>{if(current)setTestQuestions({userId:null,items:[]});});
    refresh();const timer=setInterval(refresh,30000);
    return()=>{current=false;clearInterval(timer);};
  },[user?.user_id]);
  const researchRecords=availableResearchRecords(source,!publicOnly&&testQuestions.userId===user?.user_id?testQuestions.items:[]);
  const examples=exampleResearchRecords(researchRecords);
  const [loading, setLoading] = useState(false);
  const clearResult = () => { requestGeneration.current++; setResult(null); setSavedChat(null); };
  const discovery = (value, scope) => ({searchResults:[...searchPublicSources(value,scope),...(!publicOnly&&scope!=='Kütüphane'?researchRecords.filter(x=>x.title===value):[])].slice(0,12)});
  const chooseResearchRecord = record => {const scope=['Sohbetler','Kütüphane'].includes(source)?source:researchScope;clearResult();setSource(scope);setQuery(record.title);setResult({searchResults:[record]});};

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (loading) return;
    if (!query.trim()) { toast("Lütfen bir soru yazın"); return; }
    const generation = ++requestGeneration.current;
    setLoading(true); setResult(null);
    try {
      let answer;
      if(user){
        const response=await api.post('/chats/turns',{question:query.trim(),source,public_only:publicOnly});
        if(generation===requestGeneration.current)setSavedChat(response.data.thread_id);
        const turn=response.data.turn;
        answer=turn.answer?{heading:historyCopy.answer,answer:turn.answer,evidence:turn.evidence}:discovery(query,source);
      }else answer = await askSources(api, query, source, publicOnly);
      if (generation === requestGeneration.current) setResult(answer.heading === "Kaynaklı cevap hazırlanıyor" ? discovery(query,source) : answer);
    } catch { if(generation === requestGeneration.current) setResult(discovery(query,source)); }
    finally { setLoading(false); }
  };

  const handleAction = (id) => {
    if(loading)return;
    if (id === "ornek") {
      setExamplesOpen(true);
    } else if (id === "rastgele") {
      const records = researchRecords;
      if (!records.length) {toast(suggestionsCopy[1]);return;}
      const record = records[Math.floor(Math.random() * records.length)];
      chooseResearchRecord(record);
    } else if (id === "gelismis") {
      setAdvancedOpen(true);
    } else if (id === "acik") {
      clearResult();
      setPublicOnly((v) => !v);
      toast(publicOnly ? "Erişimim olan kaynaklar" : "Herkese açık kaynaklar", {
        description: "Kaynaklı cevap hizmeti hazırlanıyor. Özel kaynaklar üyelik ve erişim onayı gerektirir.",
      });
    }
  };

  return (
    <section className="sy-image-surface relative overflow-hidden">
      {/* Arka plan görseli + perde */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.dervishPraying}
          alt=""
          className="h-full w-full object-cover object-right"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, #081411 0%, rgba(8,20,17,0.95) 32%, rgba(8,20,17,0.55) 62%, rgba(8,20,17,0.3) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(8,20,17,0.2) 0%, rgba(8,20,17,0) 40%, #081411 100%)" }}
        />
      </div>

      <div className="relative px-6 pt-10 pb-8 lg:px-10 lg:pt-12 lg:pb-10">
        <div className="max-w-3xl sy-fade-in">
          <h1 className="font-display text-[42px] font-semibold leading-[1.04] sm:text-5xl lg:text-[56px]">
            <span className="sy-gold-text">{t(heroContent.title[0])}</span>
            <br />
            <span className="sy-gold-text">{t(heroContent.title[1])}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed sm:text-base" style={{ color: "var(--sy-text)" }}>
            {t(heroContent.subtitle)}
          </p>

          {/* Arama çubuğu */}
          <form
            onSubmit={handleSubmit}
            className="mt-6 flex flex-col gap-2 rounded-2xl border p-2 sm:flex-row sm:items-center"
            style={{
              background: "rgba(255,255,255,0.97)",
              borderColor: "rgba(201,167,101,0.4)",
              boxShadow: "0 18px 50px -20px rgba(0,0,0,0.8)",
            }}
          >
            <Select value={source} onValueChange={value=>{clearResult();setSource(value);}}>
              <SelectTrigger
                aria-label={t("Kaynaklar")}
                title={t(source)}
                className="h-11 min-w-0 w-full shrink-0 border-0 bg-transparent font-medium text-[#123025] focus:ring-0 sm:w-[200px] [&>svg]:shrink-0"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                  <Layers size={16} className="shrink-0 text-[#b8954f]" />
                  <span className="min-w-0 flex-1 truncate text-left"><SelectValue>{source===researchScope?t("Mahmut Efendim AI"):t(source)}</SelectValue></span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {heroContent.filters.map((f) => (
                  <SelectItem key={f} value={f}>{t(f)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="hidden h-7 w-px bg-black/10 sm:block" />

            <div className="flex min-w-0 flex-1 items-center gap-2 px-2">
              <Search size={18} className="text-gray-400" />
              <input
                aria-label={t(heroContent.searchPlaceholder)}
                maxLength={2000}
                value={query}
                onChange={(e) => {clearResult();setQuery(e.target.value);}}
                placeholder={t(heroContent.searchPlaceholder)}
                className="h-11 min-w-0 w-full bg-transparent text-[15px] text-gray-800 outline-none placeholder:text-gray-400"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !query.trim()}
              aria-busy={loading}
              className="h-11 shrink-0 gap-2 px-6 font-semibold hover:brightness-110 transition-all"
              style={{
                background: "linear-gradient(180deg,#1a5f45 0%,#123025 100%)",
                color: "#f2e4c0",
              }}
            >
              {t("Gönder")}
              <ArrowUp size={16} />
            </Button>
          </form>

          {savedChat&&<a className="mt-4 inline-block underline" href={"/gecmis-sohbetler?chat="+encodeURIComponent(savedChat)}>{historyCopy.saved}</a>}
          {loading && <div role="status" className="mt-5 rounded-2xl border p-5" style={{background:"rgba(8,20,17,.88)",borderColor:"var(--sy-border)",color:"var(--sy-text)"}}>İsteğiniz kontrol ediliyor…</div>}
          {result && <div role="status" aria-live="polite" className="mt-5 rounded-2xl border p-5" style={{background:"rgba(8,20,17,.94)",borderColor:"var(--sy-border)",color:"var(--sy-text)"}}>
            {result.searchResults ? <><p className="font-semibold" style={{color:"var(--sy-gold-bright)"}}>{publicOnly?searchCopy.heading:t("Mevcut kaynaklarda arama")}</p><p className="mt-2 text-sm opacity-80">{result.searchResults.some(x=>x.text_ready)?suggestionsCopy[2]:source===researchScope?t("TEST soru başlıkları, sohbet bilgileri ve kitap künyeleri aranır. Bunlar AI cevabı veya doğrulanmış kitap pasajı değildir."):searchCopy.note}</p><ul className="mt-4 space-y-3">{result.searchResults.map(record=><li key={record.id} className="rounded-xl border p-4" style={{borderColor:"var(--sy-border)"}}><p className="text-xs opacity-70">{t(record.type)} · {searchCopy.levels[['Metin eşleşmesi','Katalog başlığı','Yayın bilgisi'].indexOf(record.level)]}</p><a className="mt-1 block font-semibold underline" href={record.url} rel={record.url.startsWith('https:')?'noopener noreferrer':undefined}>{record.title}</a>{record.excerpt&&<p className="mt-2 whitespace-pre-wrap text-sm" dir="auto">{record.excerpt}</p>}<p className="mt-2 text-xs opacity-60">{record.source}{record.pages?.length?" · "+t("sayfa")+": "+record.pages.join(", "):""}</p><a className="mt-2 inline-block text-sm underline" href={record.url}>{searchCopy.open} ↗</a></li>)}</ul>{!result.searchResults.length&&<p className="mt-4">{searchCopy.none}</p>}</> : result.error ? <><p className="font-semibold" style={{color:"var(--sy-gold-bright)"}}>{result.heading}</p><p className="mt-2 text-sm opacity-80">{result.error}</p></> :
            <><p className="text-xs tracking-widest" style={{color:"var(--sy-gold)"}}>KAYNAKLI CEVAP</p><p className="mt-3 leading-relaxed">{result.answer}</p><div className="mt-4 space-y-2">{result.evidence.map((ev,i)=><div key={i} className="rounded-xl border p-3 text-sm" style={{borderColor:"var(--sy-border)"}}><strong>{ev.title||ev.video_id||"Kaynak video"}</strong><div>{ev.timestamp_start||"?"} – {ev.timestamp_end||"?"}</div>{ev.transcript && <p className="mt-2 opacity-80">“{ev.transcript}”</p>}</div>)}</div></>}
          </div>}
          {/* Hızlı eylemler */}
          <div className="mt-3.5 flex flex-wrap gap-2">
            {quickActions.map((a) => {
              const Icon = a.icon;
              const isActive = a.id === "acik" && publicOnly;
              return (
                <button
                  key={a.id}
                  aria-pressed={a.id === "acik" ? publicOnly : undefined}
                  onClick={() => handleAction(a.id)}
                  className="group flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[13px] transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    background: isActive ? "rgba(201,167,101,0.16)" : "rgba(12,31,25,0.65)",
                    borderColor: isActive ? "rgba(201,167,101,0.6)" : "var(--sy-border)",
                    color: "var(--sy-text)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Icon size={15} style={{ color: "var(--sy-gold)" }} />
                  <span className="group-hover:text-[color:var(--sy-gold-bright)] transition-colors">
                    {t(a.label)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <AdvancedResearchSearch open={advancedOpen} onOpenChange={setAdvancedOpen} items={!publicOnly&&testQuestions.userId===user?.user_id?testQuestions.items:[]}/>

      {/* Örnek sorular diyaloğu */}
      <Dialog open={examplesOpen} onOpenChange={setExamplesOpen}>
        <DialogContent
          className="max-h-[85dvh] overflow-y-auto border sm:max-w-lg"
          style={{ background: "var(--sy-panel)", borderColor: "var(--sy-border)", color: "var(--sy-text)" }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl" style={{ color: "var(--sy-gold-bright)" }}>
              <Sparkles size={20} style={{ color: "var(--sy-gold)" }} />
              {t("Örnek Sorular")}
            </DialogTitle>
            <DialogDescription style={{ color: "var(--sy-text-dim)" }}>
              {suggestionsCopy[0]}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {!examples.length&&<p role="status" className="py-4 text-sm">{suggestionsCopy[1]}</p>}{examples.map((record) => (
              <button
                key={record.id}
                onClick={() => {
                  chooseResearchRecord(record);
                  setExamplesOpen(false);

                }}
                className="w-full rounded-xl border px-4 py-3 text-left text-sm transition-colors hover:bg-[color:var(--sy-panel-2)]"
                style={{ borderColor: "var(--sy-border)", color: "var(--sy-text)" }}
              >
                <span className="block text-xs opacity-70 mb-1">{t(record.type)}{record.topic!==record.title?" · "+record.topic:""}</span>{record.title}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

